import React, { Dispatch } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { ITableColumns, TemplateTableAction, TemplateTableState, TQuestion } from '../types';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from '../API';
import GenericTable from '../../Components/GenericTable/GenericTable';
import { GenericTableAction } from '../../Components/GenericTable/genericTableReducer';
import QuestionsTableRow from './QuestionsTableRow';
import { candidatesActionsListItem, headerStyle, ModalType } from '../config';
import Search from '../../SmartForms/Search';
import GenericDialog from '../../Components/Modals/GenericDialog';
import EmptyFolder from '../../../NewUI/EditJob/Questions/Modals/Icons/EmptyFolder.svg';
import { styles } from '../styles';
import { classes } from '../../Job/Applications/styles';
import NewQuestion from './Modals/NewQuestion';
import { ISnackbarInput } from '../../Components/CustomUIElements/StyledSnackbar';

export default function QuestionLibrary({
  QuestionState,
  QuestionDispatch
}: {
  QuestionState: TemplateTableState<ITableColumns>;
  QuestionDispatch: Dispatch<TemplateTableAction>;
}) {
  const {
    data: questions,
    isLoading: loadingQuestions,
    refetch: refetchQuestions
  } = useQuery({
    queryKey: ['questions', QuestionState.templateTableSettings, QuestionState.searchQuery],
    queryFn: async () =>
      await Api.getQuestions({
        page: QuestionState.templateTableSettings.page,
        limit: QuestionState.templateTableSettings.limit,
        sort: QuestionState.templateTableSettings.sort,
        sort_order: QuestionState.templateTableSettings.sort_order,
        search: QuestionState.searchQuery
      }),
    onError: () =>
      QuestionDispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting question library data`,
          state: 'error'
        }
      })
  });

  const { mutate: deleteQuestion, isLoading: deletingQuestion } = useMutation({
    mutationFn: (id: string) => Api.deleteQuestion(id),
    onSuccess: () => {
      QuestionDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Question deleted successfully', state: 'success' }
      });
      QuestionDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'delete' });
      refetchQuestions();
    },
    onError: () => {
      QuestionDispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'There was an error deleting the question', state: 'error' }
      });
    }
  });

  const newQuestionModalOpen =
    QuestionState.modalsOpen === ModalType.NEW_QUESTION ||
    QuestionState.templateTableState.actions.isOpen.edit ||
    QuestionState.templateTableState.actions.isOpen.clone;

  return (
    <Stack sx={{ rowGap: 2, paddingTop: 5 }}>
      <Box sx={classes.applicationsContainer}>
        <Box sx={classes.filtersRow}>
          <Search
            idLabel="question-library"
            placeholder="Search question by name"
            setSearch={(title) => QuestionDispatch({ type: 'SET_SEARCH_QUERY', payload: title })}
          />
          <Button
            onClick={() => {
              QuestionDispatch({ type: 'SET_MODALS_OPEN', payload: ModalType.NEW_QUESTION });
            }}
            sx={{ ...classes.newApplicationButton, marginBottom: 0.5 }}
            id="new-question-button"
          >
            New Question
          </Button>
        </Box>
        <GenericTable
          label="Question library list"
          items={(questions?.res?.library_fields || []).filter(
            (item): item is TQuestion => 'field_type' in item
          )}
          isLoadingItems={loadingQuestions}
          tableState={QuestionState.templateTableState as unknown as any}
          dispatch={QuestionDispatch as Dispatch<GenericTableAction<TQuestion>>}
          headerStyle={headerStyle}
          paginationState={QuestionState.templateTableSettings}
          pagination={{
            totalItems: Number(questions?.resHead['x-total-count']) || 0,
            totalPages: Number(questions?.resHead['x-total-pages']) || 0
          }}
          TableRows={QuestionsTableRow as unknown as any}
          TableActionList={candidatesActionsListItem}
          allowMultipleSelection={false}
          titleCellId="type"
          emptyMessageOverride={
            !questions?.res?.library_fields.length &&
            !QuestionState.searchQuery && (
              <Stack sx={styles.questionLibraryModalEmptyStateContainer}>
                <Stack component="img" src={EmptyFolder} sx={{ width: '8rem' }} />
                <Stack sx={styles.questionLibraryModalEmptyStateMessage}>
                  No questions available in the library
                </Stack>
              </Stack>
            )
          }
        />
        <GenericDialog
          isDialogOpen={QuestionState.templateTableState.actions.isOpen.delete}
          setDialogOpen={() => QuestionDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'delete' })}
          title="Permanently delete question?"
          description={
            <Stack>
              <Stack>Are you sure you want to delete this question?</Stack>
              <Stack>This action cannot be undone.</Stack>
            </Stack>
          }
          buttonCallback={() => {
            const item = QuestionState.templateTableState.actions.selectedItem;
            if (item) {
              deleteQuestion(item.id.toString());
            }
          }}
          callbackLoading={deletingQuestion}
          buttonText="Yes, delete"
          secondaryButtonText="Back to editing"
          url=""
        />
        {newQuestionModalOpen && (
          <NewQuestion
            modalOpen={newQuestionModalOpen}
            onClose={() => {
              QuestionDispatch({ type: 'SET_MODALS_OPEN', payload: null });
              QuestionDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'edit' });
              QuestionDispatch({ type: 'CLOSE_ACTION_ITEM', payload: 'clone' });
            }}
            selectedQuestion={
              'field_type' in (QuestionState.templateTableState.actions.selectedItem || {})
                ? (QuestionState.templateTableState.actions.selectedItem as TQuestion)
                : null
            }
            editingQuestion={QuestionState.templateTableState.actions.isOpen.edit}
            cloningQuestion={QuestionState.templateTableState.actions.isOpen.clone}
            setSnackbar={(payload: ISnackbarInput) =>
              QuestionDispatch({ type: 'SET_SNACKBAR', payload: payload })
            }
          />
        )}
      </Box>
    </Stack>
  );
}
